import { useState, useEffect } from 'react';
import { db } from 'lib/firebase';
import { 
    collection, 
    query, 
    where, 
    orderBy, 
    limit, 
    startAfter,
    onSnapshot, 
    addDoc, 
    updateDoc, 
    deleteDoc, 
    doc,
    serverTimestamp,
    getDocs
} from 'firebase/firestore';

/**
 * Custom hook for managing shopping list data with real-time Firestore sync
 * Generic version of useGroceryData that accepts a collection name
 */
export const useShoppingList = (collectionName = 'groceryItems') => {
    const [activeItems, setActiveItems] = useState([]);
    const [historyItems, setHistoryItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [lastHistoryDoc, setLastHistoryDoc] = useState(null);
    const [hasMoreHistory, setHasMoreHistory] = useState(true);

    // Real-time listener for active (unchecked) items
    useEffect(() => {
        const q = query(
            collection(db, collectionName),
            where('checked', '==', false),
            orderBy('createdAt', 'desc')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const items = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setActiveItems(items);
            setLoading(false);
        }, (error) => {
            console.error(`Error fetching active ${collectionName} items:`, error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [collectionName]);

    // Load initial history items (first 50)
    useEffect(() => {
        // Reset state when collection changes
        setHistoryItems([]);
        setLastHistoryDoc(null);
        setHasMoreHistory(true);
        loadHistory(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [collectionName]);

    const loadHistory = async (isInitial = false) => {
        if (!isInitial && !hasMoreHistory) return;
        
        setHistoryLoading(true);
        
        try {
            let q;
            if (isInitial || !lastHistoryDoc) {
                q = query(
                    collection(db, collectionName),
                    where('checked', '==', true),
                    orderBy('checkedAt', 'desc'),
                    limit(50)
                );
            } else {
                q = query(
                    collection(db, collectionName),
                    where('checked', '==', true),
                    orderBy('checkedAt', 'desc'),
                    startAfter(lastHistoryDoc),
                    limit(50)
                );
            }

            const snapshot = await getDocs(q);
            const items = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            if (isInitial) {
                setHistoryItems(items);
            } else {
                setHistoryItems(prev => [...prev, ...items]);
            }

            // Update pagination state
            if (snapshot.docs.length > 0) {
                setLastHistoryDoc(snapshot.docs[snapshot.docs.length - 1]);
            }
            setHasMoreHistory(snapshot.docs.length === 50);
        } catch (error) {
            console.error(`Error loading history for ${collectionName}:`, error);
        } finally {
            setHistoryLoading(false);
        }
    };

    const loadMoreHistory = () => {
        loadHistory(false);
    };

    /**
     * Add a new item
     */
    const addItem = async (name, createdBy) => {
        try {
            await addDoc(collection(db, collectionName), {
                name: name.trim(),
                checked: false,
                checkedAt: null,
                checkedBy: null,
                createdAt: serverTimestamp(),
                createdBy: createdBy,
                order: 0
            });
        } catch (error) {
            console.error(`Error adding item to ${collectionName}:`, error);
            throw error;
        }
    };

    /**
     * Check off an item
     */
    const checkItem = async (itemId, userId) => {
        try {
            const itemRef = doc(db, collectionName, itemId);
            await updateDoc(itemRef, {
                checked: true,
                checkedAt: serverTimestamp(),
                checkedBy: userId
            });
            // Reload history to include newly checked item
            loadHistory(true);
        } catch (error) {
            console.error(`Error checking item in ${collectionName}:`, error);
            throw error;
        }
    };

    /**
     * Uncheck an item (move back to active)
     */
    const uncheckItem = async (itemId) => {
        try {
            const itemRef = doc(db, collectionName, itemId);
            await updateDoc(itemRef, {
                checked: false,
                checkedAt: null,
                checkedBy: null
            });
            // Reload history to remove unchecked item
            loadHistory(true);
        } catch (error) {
            console.error(`Error unchecking item in ${collectionName}:`, error);
            throw error;
        }
    };

    /**
     * Delete an item
     */
    const deleteItem = async (itemId) => {
        try {
            await deleteDoc(doc(db, collectionName, itemId));
        } catch (error) {
            console.error(`Error deleting item from ${collectionName}:`, error);
            throw error;
        }
    };

    return {
        activeItems,
        historyItems,
        loading,
        historyLoading,
        hasMoreHistory,
        addItem,
        checkItem,
        uncheckItem,
        deleteItem,
        loadMoreHistory
    };
};
